import React, { memo } from "react";
import { Text, View, Page, Document, PDFViewer } from "@react-pdf/renderer";
import "../pdf/Fonts.js";
import { Button } from "@mui/material";
import { getMilkReportYears, summarizeYearMilk } from "../../utils/year-milk-production.js";
import { recordingMethodLabel, standardMethodLabel } from "../../screens/milkRecordings/MilkYieldResults.js";

const YearlyMilkProductionPdf = ({ selectedOwner, animalsCategory, milkData, organization, setStep }) => {
    // Inline styles
    const styles = {
        pdfViewer: { width: '85vw', height: '100vh' },
        page: {
            padding: 40,
            fontFamily: 'Roboto',
            backgroundColor: 'white'
        },
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: 20
        },
        header: {
            marginBottom: 10,
            textAlign: 'center'
        },
        title: {
            fontSize: 22,
            fontWeight: 'bold',
            marginBottom: 5
        },
        familyBox: {
            border: '1pt solid #000',
            padding: 10,
            marginBottom: 20
        },
        subheading: {
            fontSize: 18,
            fontWeight: 'bold',
            marginBottom: 10,
            marginTop: 10
        },
        table: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            marginTop: 10
        },
        tableHeader: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1pt solid #000',
            backgroundColor: '#f0f0f0',
            fontWeight: 'bold',
            fontSize: 12
        },
        tableSubHeader: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1pt solid #000',
            backgroundColor: '#f0f0f0',
            fontSize: 10,
            fontWeight: 'bold'
        },
        row: {
            display: 'flex',
            flexDirection: 'row',
            borderBottom: '1pt solid #ccc',
            minHeight: 24,
            alignItems: 'center'
        },
        codeCell: {
            width: '15%',
            padding: '5pt',
            textAlign: 'left',
            borderRight: '1pt solid #ccc',
            fontSize: 9
        },
        yearCell: {
            textAlign: 'center',
            padding: '5pt',
            borderRight: '1pt solid #ccc',
            fontSize: 10,
            fontWeight: 'bold'
        },
        dataCell: {
            textAlign: 'center',
            padding: '3pt',
            borderRight: '1pt solid #ccc',
            fontSize: 8
        },
        noDataText: {
            padding: '10pt',
            fontSize: 12,
            textAlign: 'center'
        }
    };

    const renderMilkProductionTable = () => {
        if (!milkData || milkData.length === 0) {
            return (
                <View>
                    <Text style={styles.subheading}>Δεδομένα Ετήσιας Γαλακτοπαραγωγής:</Text>
                    <Text style={styles.noDataText}>Δεν υπάρχουν διαθέσιμες γαλακτομετρήσεις για την περίοδο που επιλέξατε.</Text>
                </View>
            );
        }

        const years = getMilkReportYears();
        const yearWidth = 85 / years.length;
        const milkCell = { ...styles.dataCell, width: `${yearWidth * 0.32}%` };
        const standardMilkCell = { ...styles.dataCell, width: `${yearWidth * 0.43}%` };
        const durationCell = { ...styles.dataCell, width: `${yearWidth * 0.25}%` };

        const sortedAnimals = milkData.filter((animal) => years.some((year) => animal.yearlyProduction?.[year]?.length > 0)
            || animal.unassignedPeriods?.length > 0).sort((a, b) => String(a.registrationCode || "").localeCompare(String(b.registrationCode || "")));
        if (sortedAnimals.length === 0) {
            return (
                <View>
                    <Text style={styles.subheading}>Δεδομένα Ετήσιας Γαλακτοπαραγωγής:</Text>
                    <Text style={styles.noDataText}>Δεν υπάρχουν διαθέσιμες γαλακτομετρήσεις για την περίοδο που επιλέξατε.</Text>
                </View>
            );
        }

        return (
            <View>
                <Text style={styles.subheading}>Δεδομένα Ετήσιας Γαλακτοπαραγωγής:</Text>
                <View style={styles.table}>
                    {/* First Header Row */}
                    <View style={styles.tableHeader}>
                        <Text style={styles.codeCell}>Κωδικός Γενεαλογικού Βιβλίου</Text>
                        {years.map((year, index) => (
                            <Text key={index} style={{
                                ...styles.yearCell,
                                width: `${yearWidth}%`,
                                textAlign: 'center',
                                borderRight: index === years.length - 1 ? 'none' : '1pt solid #ccc',
                                padding: '5pt',
                                fontWeight: 'bold',
                                fontSize: 11
                            }}>{year}</Text>
                        ))}
                    </View>

                    {/* Subheader Row */}
                    <View style={styles.tableSubHeader}>
                        <Text style={styles.codeCell}></Text>
                        {years.map((_, yearIndex) => (
                            <React.Fragment key={`subheader_${yearIndex}`}>
                                <Text style={{
                                    ...milkCell,
                                    fontSize: 7
                                }}>{"Γάλα (χγρ.)"}</Text>
                                <Text style={{
                                    ...standardMilkCell,
                                    fontSize: 7
                                }}>{"Τυπική γαλακτοπαραγωγή (χγρ.)"}</Text>
                                <Text style={{
                                    ...durationCell,
                                    fontSize: 7,
                                    borderRight: yearIndex === years.length - 1 ? 'none' : '1pt solid #ccc'
                                }}>{"Μέρες Γαλ."}</Text>
                            </React.Fragment>
                        ))}
                    </View>

                    {/* Data Rows */}
                    {sortedAnimals.map((animal, animalIndex) => (
                        <View key={`animal_${animalIndex}`} style={styles.row} wrap={false}>
                            <Text style={styles.codeCell}>{animal.registrationCode}</Text>
                            {years.map((year, yearIndex) => {
                                const records = animal.yearlyProduction[year] || [];
                                const summary = summarizeYearMilk(records);
                                const format = (value) => records.length === 0 ? "" : value == null ? "—" : String(value);

                                return (
                                    <React.Fragment key={`data_${animalIndex}_${yearIndex}`}>
                                        <Text style={milkCell}>
                                            {format(summary.milk)}{summary.milk != null && summary.ongoing ? "*" : ""}{summary.milk != null && summary.partial ? "†" : ""}
                                        </Text>
                                        <Text style={standardMilkCell}>
                                            {format(summary.standardMilk)}{summary.standardMilk != null && summary.predicted ? "^" : ""}
                                        </Text>
                                        <Text style={{
                                            ...durationCell,
                                            borderRight: yearIndex === years.length - 1 ? 'none' : '1pt solid #ccc'
                                        }}>
                                            {format(summary.duration)}
                                        </Text>
                                    </React.Fragment>
                                );
                            })}
                        </View>
                    ))}
                </View>
                <Text style={{ fontSize: 9, marginTop: 8 }}>
                    Γάλα και τυπική παραγωγή σε kg. * Περιλαμβάνει ανοικτή περίοδο: παραγωγή έως τον τελευταίο έλεγχο.
                    ^ Περιλαμβάνει πρόβλεψη τυπικής παραγωγής. — Μη διαθέσιμο, εκκρεμές ή παρωχημένο αποτέλεσμα· απαιτείται έλεγχος/επανυπολογισμός.
                    † Περιλαμβάνει μερική παραγωγή.
                    Περίοδοι με μη διαθέσιμη ή παρωχημένη παραγωγή δεν αθροίζονται ως πλήρες ετήσιο σύνολο.
                </Text>
            </View>
        );
    };
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            <PDFViewer style={styles.pdfViewer}>
                <Document>
                    <Page size='A2' orientation='landscape' style={styles.page} wrap>
                        <View style={styles.container}>
                            <View style={styles.header}>
                                <Text style={styles.title}>{organization?.name}</Text>
                                <Text style={styles.title}>Δελτίο Ετήσιας Γαλακτομέτρησης</Text>
                            </View>

                            <View style={styles.familyBox}>
                                <Text>{`Μονάδα: ${selectedOwner?.lastName}`}</Text>
                                <Text>{`Κωδικός Μονάδας: ${selectedOwner?.code || "-"}`}</Text>
                                <Text>{`Διεύθυνση: ${selectedOwner?.address || "-"}`}</Text>
                                <Text>{`Νομός: ${selectedOwner?.county || "-"}`}</Text>
                            </View>

                            <View style={styles.header}>
                                <Text style={styles.title}>{animalsCategory}</Text>
                            </View>

                            {renderMilkProductionTable()}
                        </View>
                    </Page>
                    <Page size="A4" style={{ ...styles.page, paddingTop: 65, paddingBottom: 45 }} wrap>
                        <Text fixed style={{ position: "absolute", top: 25, left: 40, fontSize: 14 }}>Στοιχεία υπολογισμών γαλακτοπαραγωγής</Text>
                        <Text fixed style={{ position: "absolute", bottom: 20, right: 40, fontSize: 8 }}
                            render={({ pageNumber, totalPages }) => `Σελίδα ${pageNumber} / ${totalPages}`} />
                        {(milkData || []).flatMap((animal) => [
                            ...Object.entries(animal.yearlyProduction || {}).filter(([year]) => getMilkReportYears().includes(year))
                                .flatMap(([year, records]) => records.map((record) => ({ ...record, year }))),
                            ...(animal.unassignedPeriods || []).map((record) => ({ ...record, year: "Άγνωστη περίοδος — λείπει ο τοκετός" })),
                        ].map((record, index) => (
                            <View key={`${animal.animalId}-${record.lambingId}-${index}`} style={{ fontSize: 9, marginBottom: 10 }} wrap={false}>
                                <Text>{animal.registrationCode || "—"} · {record.year} · Τοκετός: {record.lambingDate
                                    ? new Date(record.lambingDate).toLocaleDateString("el-GR", { timeZone: "Europe/Athens" }) : "Άγνωστος"}</Text>
                                <Text>{recordingMethodLabel(record.calculation || {})} · Τυπική: {standardMethodLabel(record.calculation || {})}</Text>
                                <Text>Γάλα: {record.totalMilk == null ? "Μη διαθέσιμο" : `${record.totalMilk} kg`} · Τυπική: {record.totalStandardMilk == null ? "Μη διαθέσιμη" : `${record.totalStandardMilk} kg`}</Text>
                                <Text>{({ stale: "Απαιτείται επανυπολογισμός: άλλαξαν τα δεδομένα.", unavailable: "Ο υπολογισμός δεν είναι διαθέσιμος.", partial: "Μερική παραγωγή σύμφωνα με τον υπολογισμό του παλιού συστήματος.",
                                    "not-calculated": "Δεν έχει εκτελεστεί υπολογισμός.", legacy: "Ιστορικό αποθηκευμένο αποτέλεσμα." })[record.calculationStatus]
                                    || (record.periodState === "ongoing" ? "Ανοικτή περίοδος." : record.periodState === "closed" ? "Κλειστή περίοδος." : "Άγνωστη κατάσταση περιόδου.")}</Text>
                                {record.calculation?.lastTestDate && <Text>Τελευταίος έλεγχος: {new Date(record.calculation.lastTestDate)
                                    .toLocaleDateString("el-GR", { timeZone: "Europe/Athens" })}</Text>}
                                {record.calculatedAt && <Text>Υπολογίστηκε: {new Date(record.calculatedAt)
                                    .toLocaleDateString("el-GR", { timeZone: "Europe/Athens" })}</Text>}
                                {record.calculation?.standardDays != null && <Text>Ημέρα στόχος: {record.calculation.standardDays} από τον τοκετό · Θηλασμός: {record.calculation.sucklingDays} ημέρες · Επέκταση λήξης: {record.calculation.closingExtensionDays} ημέρες</Text>}
                                {(record.calculation?.warnings || []).map((warning, i) => <Text key={`w${i}`}>{warning}</Text>)}
                                {(record.calculation?.issues || []).map((issue, i) => <Text key={`i${i}`}>{issue.message}</Text>)}
                            </View>
                        )))}
                    </Page>
                </Document>
            </PDFViewer>
            <Button variant="outlined" color="secondary" onClick={() => setStep(1)}>
                Πίσω
            </Button>
        </div>
    );
};

export default memo(YearlyMilkProductionPdf);
